import React, { useState } from 'react';
import { videosAPI } from '../../services/api';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Modal, Button, Card, CardBody } from '../ui';

interface VideoUploadProps {
  onUploadSuccess: () => void;
  onClose: () => void;
}

const VideoUpload: React.FC<VideoUploadProps> = ({ onUploadSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: '',
    order: '',
    isRequired: true,
    isActive: true,
    language: 'ARABIC',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid video file (MP4, MPEG, MOV, AVI, WebM)');
        return;
      }
      if (file.size > 500 * 1024 * 1024) {
        setError('File size must be less than 500MB');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) { setError('Please select a video file'); return; }
    if (!formData.title.trim()) { setError('Please enter a video title'); return; }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('video', selectedFile);
      uploadFormData.append('title', formData.title);
      if (formData.description) uploadFormData.append('description', formData.description);
      if (formData.duration) uploadFormData.append('duration', formData.duration);
      if (formData.order) uploadFormData.append('order', formData.order);
      uploadFormData.append('isRequired', formData.isRequired.toString());
      uploadFormData.append('isActive', formData.isActive.toString());
      uploadFormData.append('language', formData.language);

      await videosAPI.upload(uploadFormData);
      setSuccess('Video uploaded successfully!');
      setTimeout(() => { onUploadSuccess(); onClose(); }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Upload Training Video" size="lg">
      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50 mb-4">
          <CardBody className="p-3 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-error-500 mr-2" />
            <span className="text-sm text-error-600">{error}</span>
          </CardBody>
        </Card>
      )}

      {success && (
        <Card variant="bordered" className="border-success-200 bg-success-50 mb-4">
          <CardBody className="p-3 flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-success-500 mr-2" />
            <span className="text-sm text-success-600">{success}</span>
          </CardBody>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Video File *</label>
          <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 border-dashed rounded-lg hover:border-primary-400 transition-colors">
            <div className="space-y-1 text-center">
              {selectedFile ? (
                <div className="flex items-center space-x-2">
                  <DocumentIcon className="h-8 w-8 text-primary-500" />
                  <span className="text-sm text-neutral-700 font-medium">{selectedFile.name}</span>
                </div>
              ) : (
                <>
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-neutral-400" />
                  <div className="flex text-sm text-neutral-600">
                    <label className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                      <span>Upload a video</span>
                      <input type="file" className="sr-only" accept="video/*" onChange={handleFileChange} />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-neutral-500">MP4, MOV, AVI, WebM up to 500MB</p>
                </>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Title *</label>
          <input
            type="text"
            name="title"
            required
            value={formData.title}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="Enter video title"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Description</label>
          <textarea
            name="description"
            rows={3}
            value={formData.description}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="Enter video description"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700">Duration (seconds)</label>
            <input
              type="number"
              name="duration"
              min="0"
              value={formData.duration}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              placeholder="e.g., 300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700">Order</label>
            <input
              type="number"
              name="order"
              min="0"
              value={formData.order}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
              placeholder="e.g., 1"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Language *</label>
          <select
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
          >
            <option value="ARABIC">Arabic</option>
            <option value="ENGLISH">English</option>
          </select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isRequired"
              checked={formData.isRequired}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-900">Required for training completion</label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label className="ml-2 block text-sm text-neutral-900">Active (visible to users)</label>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" isLoading={uploading}>
            Upload Video
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default VideoUpload;
